import React, { useState, useEffect } from "react";
import {
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  X,
  Zap,
} from "lucide-react";

const BulkActionsToolbar = ({
  selectedCount,
  onActivate,
  onDeactivate,
  onDelete,
  onClearSelection,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Animate in/out
  useEffect(() => {
    if (selectedCount > 0) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
      setShowDeleteConfirm(false);
    }
  }, [selectedCount]);

  const handleDelete = () => {
    if (showDeleteConfirm) {
      onDelete();
      setShowDeleteConfirm(false);
    } else {
      setShowDeleteConfirm(true);
      // Auto-reset after 4 seconds
      setTimeout(() => setShowDeleteConfirm(false), 4000);
    }
  };

  if (selectedCount === 0) return null;

  return (
    <div
      className={`mb-6 transition-all duration-300 ease-out ${
        isVisible
          ? "opacity-100 transform translate-y-0"
          : "opacity-0 transform -translate-y-4 pointer-events-none"
      }`}
    >
      <div className="relative overflow-hidden bg-gradient-to-r from-primary via-primary-dark to-primary rounded-2xl shadow-lg shadow-primary/20">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(255,255,255,0.1) 10px,
                rgba(255,255,255,0.1) 20px
              )`,
            }}
          />
        </div>

        <div className="relative px-6 py-4 flex items-center justify-between">
          {/* Left Section: Selection Info */}
          <div className="flex items-center gap-4">
            {/* Selection Count Badge */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-white text-primary text-xs font-bold rounded-full flex items-center justify-center">
                  {selectedCount}
                </span>
              </div>
              <div>
                <p className="text-white font-semibold">
                  {selectedCount} {selectedCount === 1 ? "Rule" : "Rules"}{" "}
                  Selected
                </p>
                <p className="text-white/70 text-xs">
                  Choose an action to apply
                </p>
              </div>
            </div>
          </div>

          {/* Center Section: Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Activate Button */}
            <button
              onClick={onActivate}
              className="group flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-success text-white rounded-xl font-medium transition-all duration-200 hover:shadow-lg"
            >
              <CheckCircle className="w-4 h-4 group-hover:scale-110 transition-transform" />
              <span>Activate</span>
            </button>

            {/* Deactivate Button */}
            <button
              onClick={onDeactivate}
              className="group flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-warning text-white rounded-xl font-medium transition-all duration-200 hover:shadow-lg"
            >
              <XCircle className="w-4 h-4 group-hover:scale-110 transition-transform" />
              <span>Deactivate</span>
            </button>

            {/* Divider */}
            <div className="w-px h-8 bg-white/20 mx-2" />

            {/* Delete Button */}
            <button
              onClick={handleDelete}
              className={`group flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 ${
                showDeleteConfirm
                  ? "bg-danger text-white shadow-lg shadow-danger/30 animate-pulse"
                  : "bg-white/10 hover:bg-danger/80 text-white"
              }`}
            >
              {showDeleteConfirm ? (
                <>
                  <AlertTriangle className="w-4 h-4 animate-bounce" />
                  <span>Confirm Delete</span>
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  <span>Delete</span>
                </>
              )}
            </button>
          </div>

          {/* Right Section: Clear Selection */}
          <button
            onClick={onClearSelection}
            className="flex items-center gap-2 px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
          >
            <X className="w-4 h-4" />
            <span className="text-sm font-medium">Clear</span>
          </button>
        </div>

        {/* Delete Confirmation Progress Bar */}
        {showDeleteConfirm && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
            <div
              className="h-full bg-danger animate-shrink"
              style={{
                animation: "shrink 4s linear forwards",
              }}
            />
          </div>
        )}

        {/* Keyframes for shrink animation */}
        <style jsx>{`
          @keyframes shrink {
            from {
              width: 100%;
            }
            to {
              width: 0%;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default BulkActionsToolbar;
